import type { MembershipFormatters } from './format';
import { benefitsEnd, nextChargePriceMinor } from './manage';
import { isTrialEligible, trialKey } from './trial';
import type { Membership, MembershipCharge, MembershipPlan } from './types';

/** One translated line of the screen: an i18n key, its params, an optional tone. */
export interface MembershipLine {
  key: string;
  params?: Record<string, string | number>;
  tone?: 'warning';
}

/** A charge prepared for display. */
export interface MembershipChargeRow {
  id: string;
  titleKey: string;
  amount: string;
  date: string | null;
  statusKey: string;
  isFailed: boolean;
  planName: string | null;
  /** Retry number to show; `null` for a first attempt or a charge that did not fail. */
  retryAttempt: number | null;
}

/** Source badge of a member card. */
export interface MembershipBadges {
  /** Only for memberships not bought by the customer. */
  sourceKey: string | null;
}

const CHARGE_KIND_KEYS: Record<string, string> = {
  trial_start: 'membership:chargeTrialStart',
  renewal: 'membership:chargeRenewal',
  upgrade: 'membership:chargeUpgrade',
};

const CHARGE_STATUS_KEYS: Record<string, string> = {
  pending: 'membership:chargePending',
  succeeded: 'membership:chargeSucceeded',
  failed: 'membership:chargeFailed',
};

const SOURCE_KEYS: Record<string, string> = {
  cms_grant: 'membership:sourceCmsGrant',
  vip: 'membership:sourceVip',
};

/**
 * Status line of a `past_due` membership, by `paymentIssue`. A card that can't
 * be charged is never retried, so a customer who added the card (`self`) is
 * asked for a new one by the cancel date; a soft decline names the next
 * attempt and the cancel date for any source, since the backend retries it
 * on its own. A date the backend did not give is left out, and without
 * `paymentIssue`, or for an unusable card of a membership the customer did
 * not pay for, nothing is promised.
 */
const pastDueLine = (
  membership: Membership,
  f: MembershipFormatters,
): MembershipLine => {
  const issue = membership.paymentIssue ?? null;
  const isSelf = membership.source === 'self';
  const endsOn = f.date(issue?.cancelsAt ?? null);
  if (issue?.cardUnusable && isSelf) {
    return endsOn
      ? {
          key: 'membership:pastDueCardUnusable',
          params: { date: endsOn },
          tone: 'warning',
        }
      : { key: 'membership:pastDueCardUnusableNoDate', tone: 'warning' };
  }
  if (issue && !issue.cardUnusable) {
    const retryOn = f.date(issue.nextAttemptAt);
    if (retryOn && endsOn) {
      return {
        key: 'membership:pastDueRetry',
        params: { retryDate: retryOn, date: endsOn },
        tone: 'warning',
      };
    }
    if (retryOn) {
      return {
        key: 'membership:pastDueRetryNoEnd',
        params: { retryDate: retryOn },
        tone: 'warning',
      };
    }
    if (endsOn) {
      return {
        key: 'membership:pastDueEndsOn',
        params: { date: endsOn },
        tone: 'warning',
      };
    }
  }
  return {
    key: isSelf ? 'membership:pastDueUpdateCard' : 'membership:pastDue',
    tone: 'warning',
  };
};

/**
 * Price of the charge due at `chargeAt`: the scheduled plan's once the switch
 * lands by then, else the next charge price `/me` states. Falls back to it
 * while the scheduled plan is not among `plans`.
 */
const chargePriceMinor = (
  membership: Membership,
  plans: readonly MembershipPlan[],
  chargeAt: string | null,
): number => {
  const change = membership.scheduledChange;
  const target = change && plans.find((plan) => plan.id === change.planId);
  const lands =
    target &&
    chargeAt !== null &&
    Date.parse(change.changeAt) <= Date.parse(chargeAt);

  return lands ? target.priceMinor : nextChargePriceMinor(membership);
};

const statusLines = (
  membership: Membership,
  f: MembershipFormatters,
  plans: readonly MembershipPlan[],
): MembershipLine[] => {
  const price = (chargeAt: string | null) =>
    f.money(
      chargePriceMinor(membership, plans, chargeAt),
      membership.plan.currency,
    );

  switch (membership.status) {
    case 'trialing': {
      const date = f.date(membership.trialEnd);
      return date
        ? [
            { key: 'membership:trialUntil', params: { date } },
            {
              key: 'membership:firstCharge',
              params: { price: price(membership.trialEnd), date },
            },
          ]
        : [];
    }
    case 'active': {
      const next = f.date(membership.nextChargeAt);
      if (next) {
        return [
          {
            key: 'membership:renewsOn',
            params: { date: next, price: price(membership.nextChargeAt) },
          },
        ];
      }
      const until = f.date(membership.currentPeriodEnd);
      return until
        ? [{ key: 'membership:activeUntil', params: { date: until } }]
        : [];
    }
    case 'past_due':
      return [pastDueLine(membership, f)];
    case 'cancelled': {
      const date = f.date(benefitsEnd(membership));
      return date
        ? [{ key: 'membership:cancelledUntil', params: { date } }]
        : [];
    }
    default:
      return [];
  }
};

const RENEWAL_KEYS = new Set(['membership:renewsOn', 'membership:firstCharge']);

/**
 * "Plan is being retired" line of a membership that ends at `endsAt`; a
 * warning once the benefits have already stopped. Not for a cancelled one.
 */
const endsAtLine = (
  membership: Membership,
  f: MembershipFormatters,
): MembershipLine | null => {
  const date = f.date(membership.endsAt ?? null);
  if (!date || membership.status === 'cancelled') {
    return null;
  }
  return membership.benefitsActive
    ? { key: 'membership:planRetiring', params: { date } }
    : {
        key: 'membership:planRetiredBenefitsStopped',
        params: { date },
        tone: 'warning',
      };
};

/**
 * Status captions of a member, followed by the scheduled plan switch if any.
 * A membership with `endsAt` does not renew, so its renewal lines give way to
 * the end date. `plans` give the price of a scheduled plan the next charge
 * goes to.
 */
export const memberStatusLines = (
  membership: Membership,
  f: MembershipFormatters,
  plans: readonly MembershipPlan[] = [],
): MembershipLine[] => {
  const ending = endsAtLine(membership, f);
  const lines = ending
    ? [
        ...statusLines(membership, f, plans).filter(
          (line) => !RENEWAL_KEYS.has(line.key),
        ),
        ending,
      ]
    : statusLines(membership, f, plans);
  const change = membership.scheduledChange;
  const changeDate = change ? f.date(change.changeAt) : null;

  if (change && changeDate) {
    lines.push({
      key: 'membership:switching',
      params: { planName: change.planName, date: changeDate },
    });
  }
  return lines;
};

/** Plan terms: trial (for this customer only), free-delivery threshold, discount steps and the markdown rule. */
export const planTermLines = (
  plan: MembershipPlan,
  f: MembershipFormatters,
): MembershipLine[] => {
  const lines: MembershipLine[] = [];
  if (isTrialEligible(plan)) {
    lines.push({
      key: trialKey('membership:trial', plan.trialUnit),
      params: { count: plan.trialLength },
    });
  }

  if (plan.freeDeliveryThresholdMinor === 0) {
    lines.push({ key: 'membership:freeDeliveryAlways' });
  } else if (typeof plan.freeDeliveryThresholdMinor === 'number') {
    lines.push({
      key: 'membership:freeDeliveryFrom',
      params: {
        threshold: f.money(plan.freeDeliveryThresholdMinor, plan.currency),
      },
    });
  }

  const tiers = (plan.discountTiers ?? [])
    .filter((tier) => tier.percent > 0)
    .sort((a, b) => a.floorMinor - b.floorMinor);

  for (const tier of tiers) {
    lines.push(
      tier.floorMinor > 0
        ? {
            key: 'membership:tierFrom',
            params: {
              percent: tier.percent,
              floor: f.money(tier.floorMinor, plan.currency),
            },
          }
        : {
            key: 'membership:tierEveryOrder',
            params: { percent: tier.percent },
          },
    );
  }

  if (tiers.length > 0) {
    lines.push({
      key: plan.appliesToMarkdown
        ? 'membership:markdownIncluded'
        : 'membership:markdownExcluded',
    });
  }
  return lines;
};

const chargeStatusKey = (charge: MembershipCharge): string => {
  if (
    charge.kind === 'trial_start' &&
    charge.status === 'succeeded' &&
    charge.amountMinor === 0
  ) {
    return 'membership:chargeTrial';
  }
  return CHARGE_STATUS_KEYS[charge.status] ?? 'membership:chargePending';
};

/**
 * Prepares one payment-history row; the date is the settlement time, else the
 * creation time. A settled free trial start reads "Trial" rather than
 * "Paid"; a trial start that charged an amount reads "Paid".
 */
export const chargeRow = (
  charge: MembershipCharge,
  f: MembershipFormatters,
): MembershipChargeRow => ({
  id: charge.id,
  titleKey: CHARGE_KIND_KEYS[charge.kind] ?? 'membership:chargeOther',
  amount: f.money(charge.amountMinor, charge.currency),
  date: f.date(charge.settledAt ?? charge.createdAt),
  statusKey: chargeStatusKey(charge),
  isFailed: charge.status === 'failed',
  planName: charge.planName,
  retryAttempt:
    charge.status === 'failed' && charge.attempt > 1 ? charge.attempt : null,
});

/** Badges of a member card. */
export const memberBadges = (membership: Membership): MembershipBadges => ({
  sourceKey: SOURCE_KEYS[membership.source] ?? null,
});

/** Stable React key of a line; lines sharing an i18n key differ by their params. */
export const lineId = (line: MembershipLine): string =>
  [line.key, ...Object.values(line.params ?? {})].join('|');
